import { type CSSProperties, type ReactNode, useEffect, useState } from "react";

import { cn } from "@btnet/utilities-core";

import "./BuiFloatingActionBar.less";

/**
 * Props for the {@link BuiFloatingActionBar} component — the blueprint (BUI)
 * floating action bar primitive.
 *
 * This is a presentational positioning/animation primitive only: it fixes a
 * pill-shaped bar to the bottom-center of the viewport and animates it in/out.
 * It has no knowledge of selection, actions, or overflow — those concerns live
 * in the app-facing composite (`BTFloatingActionBar` in `@btnet/core-components`).
 */
export interface IBuiFloatingActionBarProps {
  /**
   * Controls whether the bar is shown. When toggled, the bar plays its
   * slide-up/fade-in or slide-down/fade-out animation. While `false` (and once
   * the exit animation completes) nothing is rendered.
   */
  isOpen: boolean;

  /**
   * The bar's content — typically a selected-count label and action buttons.
   * The primitive lays children out in a horizontal, vertically-centered row.
   */
  children: ReactNode;

  /**
   * Accessible label for the bar's toolbar region. Defaults to `"Actions"`.
   * @default "Actions"
   */
  ariaLabel?: string | undefined;

  /**
   * Additional class name(s) to apply to the bar for styling purposes.
   */
  className?: string;

  /**
   * Additional inline styles to apply to the bar.
   */
  style?: CSSProperties;

  /**
   * The required data-testid so it can be granular for testing.
   */
  testid: string;
}

/**
 * BuiFloatingActionBar - the blueprint (BUI) floating action bar primitive.
 *
 * Renders a pill-shaped bar fixed to the bottom-center of the viewport that
 * slides up/fades in when opened and slides down/fades out when closed. It is a
 * "dumb" positioning + animation primitive: it owns no selection, action, or
 * overflow logic and carries no domain knowledge. Compose it with actions in the
 * app-facing `BTFloatingActionBar` (`@btnet/core-components`) instead of using it
 * directly in app code.
 *
 * While closed (and after its exit animation completes) the bar renders nothing,
 * so it stays out of the accessibility tree when there is nothing to act on.
 *
 * @param props - {@link IBuiFloatingActionBarProps} controlling visibility, content, and styling.
 * @returns The rendered bar, or `null` while fully closed.
 * @example
 * <BuiFloatingActionBar isOpen={selectedCount > 0} testid="bill-fab">
 *   <span>{selectedCount} selected</span>
 *   <BuiButton testid="delete" type="tertiary">Delete</BuiButton>
 * </BuiFloatingActionBar>
 */
export const BuiFloatingActionBar = ({
  isOpen,
  children,
  ariaLabel = "Actions",
  className,
  style,
  testid,
}: IBuiFloatingActionBarProps) => {
  // Keep the bar mounted through its exit animation: `isOpen` flips to false
  // immediately, but we only unmount once the out-animation has finished.
  const [isMounted, setIsMounted] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);

  const handleAnimationEnd = (): void => {
    if (!isOpen) {
      setIsMounted(false);
    }
  };

  if (!isMounted && !isOpen) {
    return null;
  }

  return (
    <div
      role="toolbar"
      aria-label={ariaLabel}
      data-testid={testid}
      className={cn(
        "fixed bottom-12 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4 rounded-md border border-blue-80 bg-blue-100 px-5 py-2 font-sans text-sm font-normal text-blue-5 shadow-floating",
        {
          "bui-floating-action-bar--in-animation": isOpen,
          "bui-floating-action-bar--out-animation": !isOpen,
        },
        className,
      )}
      style={style}
      onAnimationEnd={handleAnimationEnd}
    >
      {children}
    </div>
  );
};
